import { AddressForm } from "@/forms/address-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { CountryFlagIcon } from "@msuite/ui/country-flag-icon";
import { ColorPicker, Select, TextInput } from "@msuite/ui/form";
import { icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { countryList } from "@msuite/utils/misc";
import { type PatchOrganizationSchema, patchOrganizationSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { FormProvider, useForm } from "react-hook-form";

export function OrganizationGeneralInformationForm() {
	/** Hooks */
	const { t } = useTranslation();
	const { organization, refetch } = useAuth();
	const qc = useQueryClient();

	/** Query */
	const methods = useForm({
		resolver: resolver(patchOrganizationSchema),
		defaultValues: async () => {
			if (!organization) return {} as PatchOrganizationSchema;
			const response = await client.organizations[":orgId"].$get({
				param: {
					orgId: guardString(organization.id),
				},
			});
			const data = await responseHandler(response);
			if (!data.organization) return {} as PatchOrganizationSchema;
			return data.organization as unknown as PatchOrganizationSchema;
		},
	});

	/** Functions */
	async function handleOnSubmit(json: PatchOrganizationSchema) {
		if (!organization?.id) return;
		await client.organizations[":orgId"].$patch({
			param: { orgId: organization?.id },
			json,
		});
		await qc.invalidateQueries({
			predicate: invalidate(["organizations"]),
		});
		await refetch();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<ContentCard
				icon={icon("ArrowUp01")}
				title={t("organization.organization")}
			>
				<form
					onSubmit={methods.handleSubmit(handleOnSubmit)}
					className="grow-0 grid grid-rows-2 -mx-4 -mt-4 h-[50vh] min-h-0 overflow-hidden"
					style={{
						gridTemplateRows: "1fr max-content",
					}}
				>
					<div className="bottom-space grow-0 gap-6 px-4 pt-4 h-full min-h-0 overflow-y-auto v-stack">
						<TextInput
							label={t("organization.organization")}
							isRequired
							placeholder={t("placeholders.organization")}
							control={methods.control}
							name="name"
						/>
						<AddressForm />
						<Select
							label={t("organization.organization_size")}
							isNullable={false}
							isRequired
							control={methods.control}
							name="size"
							options={[
								{ label: "1-10", value: "1-10", item: null },
								{ label: "11-50", value: "11-50", item: null },
								{ label: "51-200", value: "51-200", item: null },
								{ label: "201-500", value: "201-500", item: null },
								{ label: "501-1000", value: "501-1000", item: null },
								{ label: "1001-5000", value: "1001-5000", item: null },
								{ label: "5000+", value: "5000+", item: null },
							]}
							itemNotFoundLabel={undefined}
						/>
						<Select
							label={t("address.country")}
							isRequired
							isNullable={false}
							control={methods.control}
							name="country"
							options={countryList.map((country) => ({
								label: country.name,
								value: country.code,
								item: country,
							}))}
							renderOption={(option) => (
								<div className="h-stack">
									<CountryFlagIcon locale={option.code} />
									<p>{option.name}</p>
								</div>
							)}
							itemNotFoundLabel={undefined}
						/>
						<ColorPicker
							control={methods.control}
							isRequired
							name="color"
							label={t("organization.organization_color")}
						/>
					</div>
					<div className="justify-end -mb-2 p-4 border-color border-t h-stack">
						<Button
							type="submit"
							isLoading={methods.formState.isSubmitting}
							variant="primary"
							isDisabled={!methods.formState.isDirty}
						>
							{t("general.save")}
						</Button>
					</div>
				</form>
			</ContentCard>
		</FormProvider>
	);
}
